import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Package, Calendar, MapPin, Phone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/orders/my-orders`,
        { withCredentials: true }
      );
      setOrders(data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Please login to view your orders");
      } else {
        toast.error("Failed to load orders");
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    confirmed: "bg-blue-100 text-blue-800",
    preparing: "bg-purple-100 text-purple-800",
    ready: "bg-green-100 text-green-800",
    out_for_delivery: "bg-orange-100 text-orange-800",
    delivered: "bg-emerald-100 text-emerald-800",
    picked_up: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-red-100 text-red-800"
  };

  const getStatusText = (status) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">My Orders</h1>
          <p className="text-gray-600">Track your meal prep orders</p>
        </div>

        {orders.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No orders yet</h2>
              <p className="text-gray-600 mb-6">Start ordering delicious meals from Chef Eric!</p>
              <Link to="/meal-prep">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Browse Menu
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <Card key={order.id} className="border-2 hover:border-emerald-200 transition-colors">
                <CardHeader className="bg-stone-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{order.order_number}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        Ordered on {new Date(order.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                    <Badge className={statusColors[order.status] || "bg-gray-100 text-gray-800"}>
                      {getStatusText(order.status)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center">
                        <Package className="h-4 w-4 mr-2 text-emerald-600" />
                        Order Items
                      </h3>
                      <div className="space-y-2">
                        {order.items.map((item, idx) => (
                          <div key={`${order.id}-item-${item.menu_item_id || idx}-${idx}`} className="flex justify-between text-sm">
                            <span>{item.quantity}x {item.name}</span>
                            <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3 flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-emerald-600" />
                        Fulfillment Details
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>Type:</strong> {order.fulfillment_type === 'delivery' ? 'Delivery' : 'Pickup'}
                        </p>
                        <p>
                          <strong>Date:</strong> {new Date(order.scheduled_date).toLocaleDateString()}
                        </p>
                        {order.fulfillment_type === 'delivery' && order.delivery_address && (
                          <p className="flex items-start">
                            <MapPin className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0 text-emerald-600" />
                            <span>
                              {order.delivery_address.street}, {order.delivery_address.city}, {order.delivery_address.state} {order.delivery_address.zip_code}
                            </span>
                          </p>
                        )}
                        <p className="flex items-center">
                          <Phone className="h-4 w-4 mr-1 text-emerald-600" />
                          {order.customer_phone}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <div className="flex justify-between items-center">
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span>${order.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tax:</span>
                        <span>${order.tax.toFixed(2)}</span>
                      </div>
                      {order.delivery_fee > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Delivery Fee:</span>
                          <span>${order.delivery_fee.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Total</p>
                      <p className="text-3xl font-bold text-emerald-600">${order.total.toFixed(2)}</p>
                    </div>
                  </div>

                  {order.notes && (
                    <>
                      <Separator className="my-4" />
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Special Instructions:</p>
                        <p className="text-sm text-gray-600">{order.notes}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
